import React, {
  FC,
  useContext,
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { Modal, Button, BigRadio, Group, Tabs } from '../../index';
import {
  AuthStore,
  updateAuthIndex,
  updateAuth,
  BrandStore,
  RegistrationStore,
  updateRegistration,
} from '../../../context';
import { useWindowSize, useToaster } from '../../../hooks';
import { formatDateWithTimezone } from '../../../util/string.util';
import { OptionContent } from './OptionContent';
import { StatusLabel } from './StatusLabel';
import {
  createTeam,
  groupMerchantsByStatus,
  getTabCounts,
  getFilteredAndSortedMerchants,
  StatusTabType,
} from './MerchantSwitcherModal.util';
import { history } from '../../../history';
import { merchantIsConditionalApproved } from '../../../util/registration.util';
import styled from 'styled-components';

type TabOption = {
  label: string;
  value?: unknown;
};

interface MerchantSwitcherModalProps
  extends React.ComponentProps<typeof Modal> {
  /**
   * Invoked when the user requests to clone the currently selected merchant.
   * Parent is responsible for transitioning modal state to 'clone'.
   */
  onRequestClone: (selectedIndex: number) => void;
}

const ScrollableContent = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  background: white;
`;

const StickyFooter = styled.div`
  padding: 16px;
  background: var(--gray-0, #fff);
  border-radius: 0 0 6px 6px;
  box-shadow: 0 0 13.7px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: sticky;
  bottom: 0;
  width: 95%;
`;

export const MerchantSwitcherModal: FC<MerchantSwitcherModalProps> = props => {
  const { isMobile } = useWindowSize();
  const [selectedAuthIndex, setSelectedAuthIndex] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<StatusTabType>('Pending');
  const onSubmitRef = useRef<() => void>();
  const adjustAuthIndexRef = useRef<(adjustment: number) => void>();
  const { toast, toaster } = useToaster();
  const [state, setState] = useState<'idle' | 'creating' | 'cloning'>('idle');
  const shouldRedirectRef = useRef(true);
  const { dispatch: registrationDispatch } = useContext(RegistrationStore);
  const {
    dispatch: authDispatch,
    state: { auth },
  } = useContext(AuthStore);
  const {
    state: { brand },
  } = useContext(BrandStore);
  const { isOpen, onClose, onRequestClone } = props;

  const authIndex = auth?.index;
  const authToken = auth?.token;
  const authData = useMemo(() => auth?.data || [], [auth?.data]);

  // Group and filter merchants based on active tab
  const groupedMerchants = groupMerchantsByStatus(authData);
  const tabCounts = getTabCounts(groupedMerchants);
  const filteredMerchants = getFilteredAndSortedMerchants(authData, activeTab);

  const selectedMerchant =
    filteredMerchants && filteredMerchants[selectedAuthIndex]?.merchant;

  // Keyboard shortcuts for selection & submit
  const onKeyDown = useCallback((e: KeyboardEvent) => {
    switch (e.keyCode) {
      case 13: // Enter
        e.preventDefault();
        if (onSubmitRef.current) onSubmitRef.current();
        break;
      case 38: // Up
        e.preventDefault();
        if (adjustAuthIndexRef.current) adjustAuthIndexRef.current(-1);
        break;
      case 40: // Down
        e.preventDefault();
        if (adjustAuthIndexRef.current) adjustAuthIndexRef.current(+1);
        break;
      default:
        break;
    }
  }, []);

  const onIsOpenChanged = useCallback(
    (isOpen: boolean) => {
      window.removeEventListener('keydown', onKeyDown);

      if (isOpen) {
        window.addEventListener('keydown', onKeyDown);

        // reset back to actual selected auth index when user opens modal
        // because this component isn't unmounted by the parent unless auth.data.length === 0
        // and making a selection, canceling, and reopening the modal will result in an inaccurate selection
        if (!isNaN(authIndex!)) {
          // Determine the correct initial tab based on what merchants exist
          const counts = getTabCounts(groupMerchantsByStatus(authData));
          let initialTab: StatusTabType = 'Pending';
          if (counts.Pending > 0) {
            initialTab = 'Pending';
          } else if (counts.Active > 0) {
            initialTab = 'Active';
          } else if (counts.Inactive > 0) {
            initialTab = 'Inactive';
          } else if (counts.All > 0) {
            initialTab = 'All';
          }
          setActiveTab(initialTab);
          setSelectedAuthIndex(0);
        }
      } else {
        window.removeEventListener('keydown', onKeyDown);
      }

      return () => window.removeEventListener('keydown', onKeyDown);
    },
    [onKeyDown, authIndex, authData],
  );

  const handleCreateMerchant = useCallback(async () => {
    setState('creating');

    try {
      const teamResult = await createTeam(authToken!, selectedMerchant!);
      const newAuthData = teamResult.authData;
      registrationDispatch(updateRegistration(teamResult.newRegistration));

      authDispatch(updateAuth(newAuthData));
      authDispatch(updateAuthIndex(0));
      history.push('/welcome');
    } catch (error) {
      toaster(toast.error(error, 'There was a problem creating the merchant.'));
    } finally {
      setState('idle');
    }
  }, [
    selectedMerchant,
    authToken,
    authDispatch,
    toast,
    toaster,
    registrationDispatch,
  ]);
  useEffect(() => onIsOpenChanged(isOpen), [isOpen, onIsOpenChanged]);

  // Wrap selection index on arrow navigation
  useEffect(() => {
    return onIsOpenChanged(isOpen);
  }, [isOpen, onIsOpenChanged]);

  useEffect(() => {
    // have to use a ref here so that selectedAuthIndex and filteredMerchants
    // are not stale when onKeyDown calls this fucntion
    adjustAuthIndexRef.current = (adjustment: number) => {
      let index = selectedAuthIndex + adjustment;
      // if new index is greater than the length of the merchant data, wrap around to 0
      index = index > filteredMerchants.length - 1 ? 0 : index;
      // if new index is less than 0, wrap around to the last index of the merchant data
      index = index < 0 ? filteredMerchants.length - 1 : index;
      setSelectedAuthIndex(index);
    };
  }, [selectedAuthIndex, filteredMerchants]);

  // Prepare submit logic (redirect if ACTIVE)
  useEffect(() => {
    // have to use a ref here so that selectedAuthIndex, merchants, and onClose
    // are not stale when onKeyDown calls this function
    onSubmitRef.current = () => {
      const selectedMerchantData = filteredMerchants[selectedAuthIndex];

      // For grouped modal, find the actual auth index of the selected merchant
      const actualAuthIndex = authData.findIndex(
        datum => datum === selectedMerchantData,
      );
      authDispatch(updateAuthIndex(actualAuthIndex));

      // if merchant is active, redirect to Connect app
      // since that means they are approved and done using the self reg app
      const merchant = selectedMerchantData?.merchant;
      if (merchant?.status === 'ACTIVE' && shouldRedirectRef.current) {
        // Find the selected merchant auth in the original authData
        const authDataCopy = [...authData];
        const actualSelectedAuthIndex = authDataCopy.findIndex(
          datum => datum === selectedMerchantData,
        );

        // Place selected merchant auth at index 0 of the auth data
        // so that fattpay-react logs the user in with the merchant they selected
        const [selectedMerchantAuth] = authDataCopy.splice(
          actualSelectedAuthIndex,
          1,
        );
        authDataCopy.unshift(selectedMerchantAuth);

        const search = authDataCopy.reduce((sum, datum, index) => {
          return sum + (index === 0 ? '?' : '&') + 'jwt[]=' + datum.token;
        }, '');

        window.location.href = `${process.env.REACT_APP_OMNI_APP_URL}/#/sso${search}`;
      }

      onClose();
    };
  }, [selectedAuthIndex, authData, filteredMerchants, authDispatch, onClose]);

  if (!authData.length) return null;

  const renderMerchantList = () => {
    // Build tab options - only show tabs that have merchants
    const tabOptions: TabOption[] = [];
    if (tabCounts.All > 0) tabOptions.push({ label: 'All', value: 'All' });
    if (tabCounts.Pending > 0)
      tabOptions.push({ label: 'Pending', value: 'Pending' });
    if (tabCounts.Active > 0)
      tabOptions.push({ label: 'Active', value: 'Active' });
    if (tabCounts.Inactive > 0)
      tabOptions.push({ label: 'Inactive', value: 'Inactive' });
    return (
      <>
        <Tabs
          options={tabOptions}
          defaultSelected={activeTab}
          onChange={(_, newValue) => {
            setActiveTab(newValue.value as StatusTabType);
            setSelectedAuthIndex(0); // Reset to first merchant when switching tabs
          }}
        />
        <ScrollableContent>
          <BigRadio
            id="merchant-switcher-radio-grouped"
            value={String(selectedAuthIndex)}
            disabled={state !== 'idle'}
            onChange={authIndex => {
              setSelectedAuthIndex(+authIndex);
            }}
          >
            {filteredMerchants.map((datum, i) => (
              <BigRadio.Option
                key={`${datum.merchant?.id}-${i}`}
                value={String(i)}
              >
                <OptionContent>
                  <div>
                    <span className="title">
                      {datum.merchant!.company_name}
                    </span>
                    <span className="subtext">
                      Created{' '}
                      {formatDateWithTimezone(datum.merchant!.created_at)}
                    </span>
                  </div>
                  <div>
                    <StatusLabel
                      className={datum.merchant!.status.toLowerCase()}
                    >
                      {datum.merchant!.status}
                    </StatusLabel>
                    {datum.merchant?.registration?.signature_status ===
                      'PENDING RE-SIGNATURE' && (
                      <StatusLabel className="warning">RE-SIGN</StatusLabel>
                    )}
                    {merchantIsConditionalApproved(datum.merchant!) && (
                      <StatusLabel className="warning">
                        CONDITIONALLY APPROVED
                      </StatusLabel>
                    )}
                  </div>
                </OptionContent>
              </BigRadio.Option>
            ))}
          </BigRadio>
        </ScrollableContent>
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      isClosable={false}
      onClose={onClose}
      width="40rem"
      title="Select Company"
      id="merchant-switcher"
      data-testid="merchant-switcher"
    >
      <div style={{ padding: '16px', paddingBottom: '0' }}>
        {renderMerchantList()}
      </div>
      <StickyFooter>
        <Group
          justify="flex-end"
          space="8px"
          direction={isMobile ? 'column' : 'row'}
        >
          {brand?.allowAddingLocation ? (
            <Button
              motif="accent"
              size="small"
              id="merchant-switcher-create-button"
              data-testid="merchant-switcher-create-button"
              variant="outline"
              icon="fas fa-plus"
              loading={state === 'creating'}
              disabled={state !== 'idle'}
              onClick={() => handleCreateMerchant()}
            >
              <span className="-mobile-only">New</span>
              <span className="-mobile-hide">Create New</span>
            </Button>
          ) : null}
          {brand?.allowCloningLocation ? (
            <Button
              motif="tertiary"
              size="small"
              id="merchant-switcher-clone-button"
              data-testid="merchant-switcher-clone-button"
              variant="outline"
              icon="fas fa-clone"
              disabled={state !== 'idle'}
              onClick={() => {
                // For grouped modal, we need to map from filtered index to actual auth index
                const selectedMerchantData =
                  filteredMerchants[selectedAuthIndex];
                const actualAuthIndex = authData.findIndex(
                  datum => datum === selectedMerchantData,
                );
                authDispatch(updateAuthIndex(actualAuthIndex));
                onRequestClone(actualAuthIndex);
              }}
              loading={state === 'cloning'}
            >
              <span className="-mobile-only">Clone</span>
              <span className="-mobile-hide">Clone Selected</span>
            </Button>
          ) : null}
          {selectedMerchant &&
            selectedMerchant.status === 'ACTIVE' &&
            (merchantIsConditionalApproved(selectedMerchant) ||
              selectedMerchant.registration?.signature_status ===
                'PENDING RE-SIGNATURE') && (
              <Button
                size="small"
                id="merchant-switcher-documents-button"
                data-testid="merchant-switcher-documents-button"
                disabled={state !== 'idle'}
                onClick={() => {
                  if (merchantIsConditionalApproved(selectedMerchant)) {
                    history.push('/documents');
                  } else {
                    // For merchants pending re-signature, take user to review page where they can see the outstanding documents that need to be re-signed
                    // We also need to set the selected merchant in the auth context so that the review page knows which merchant's documents to show that's what onSubmitRef is for
                    shouldRedirectRef.current = false;
                    onSubmitRef.current!();
                    history.push('/review');
                  }
                }}
                icon="fas fa-exchange-alt"
                motif="accent"
              >
                <span>Continue</span>
              </Button>
            )}
          <Button
            size="small"
            id="merchant-switcher-continue-button"
            data-testid="merchant-switcher-continue-button"
            disabled={state !== 'idle'}
            onClick={() => onSubmitRef.current!()}
            icon={
              selectedMerchant?.status === 'ACTIVE'
                ? 'fas fa-external-link-alt'
                : 'fas fa-exchange-alt'
            }
            motif={selectedMerchant?.status === 'ACTIVE' ? 'success' : 'accent'}
          >
            <span>
              {selectedMerchant?.status === 'ACTIVE'
                ? `Go to ${brand?.platformName || 'Stax Pay'}`
                : 'Continue'}
            </span>
          </Button>
        </Group>
      </StickyFooter>
    </Modal>
  );
};
